<template>
  <div class="hall">
    <div v-if="state === 'loading'" class="hall__loading">加载赛程中...</div>
    <div v-else-if="state === 'error'" class="hall__error">
      <span>赛程加载失败</span>
      <button @click="load">重试</button>
    </div>
    <div v-else class="hall__content">
      <div ref="tabs" class="hall__tabs">
        <button
          v-for="group in matchGroups"
          :key="group.key"
          :ref="`tab-${group.key}`"
          :class="{ active: activeKey === group.key }"
          @click="selectGroup(group.key)"
        >
          <span class="hall__tab-date">{{ group.dateLabel }}</span>
          <small class="hall__tab-stage">{{ group.stageLabel }}</small>
        </button>
      </div>

      <div v-if="activeMatches.length" class="hall__cards">
        <match-card
          v-for="match in activeMatches"
          :key="match.match_key"
          :match="match"
          :ticket-coin="currentTicketCoin"
          :selected-side="selectedSide(match)"
          @choose="openBet"
        ></match-card>
      </div>
      <div v-else class="hall__empty">暂无赛程</div>
    </div>

    <bet-dialog
      :show="betDialog.show"
      :match="betDialog.match"
      :side="betDialog.side"
      :ticket-coin="betDialog.ticketCoin"
      :coin-balance="betDialog.coinBalance"
      :submitting="submitting"
      @cancel="closeBet"
      @confirm="confirmBet"
    />

    <celebrate-dialog
      :show="celebrate.show"
      :items="celebrate.items"
      @close="celebrate.show = false"
      @go-lottery="goLottery"
    />
  </div>
</template>

<script>
import { Toast } from 'vant'
import { bet, createIdempotencyKey, getBalance, getCelebrations, getMatches } from '@/api/worldcupQuiz'
import BetDialog from '../components/BetDialog.vue'
import CelebrateDialog from '../components/CelebrateDialog.vue'
import MatchCard from '../components/MatchCard.vue'
import { isBettable, roundLabel, roundStageName, unwrapResponse, waitForNativeTouchEnd } from '../utils'
import { teamLogosFromMatches } from '../../worldcup2026/utils/teamFlag'

export default {
  name: 'WorldCupQuizHall',
  components: { BetDialog, CelebrateDialog, MatchCard },
  props: {
    entry: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      state: 'loading',
      matches: [],
      activeKey: '',
      latestTicketCoin: null,
      submitting: false,
      balanceLoading: false,
      betDialog: {
        show: false,
        match: null,
        side: 0,
        ticketCoin: 0,
        coinBalance: 0
      },
      celebrate: {
        show: false,
        items: []
      }
    }
  },
  computed: {
    matchGroups () {
      const groups = []
      this.matches.forEach(match => {
        const key = this.tabGroupKey(match)
        if (!groups.some(group => group.key === key)) {
          groups.push({
            key,
            dateLabel: this.tabDateLabel(match),
            stageLabel: this.tabStageLabel(match)
          })
        }
      })
      return groups
    },
    activeMatches () {
      return this.matches.filter(item => this.tabGroupKey(item) === this.activeKey)
    },
    currentTicketCoin () {
      return this.latestTicketCoin !== null ? this.latestTicketCoin : Number(this.entry.ticket_coin) || 0
    }
  },
  watch: {
    'entry.ticket_coin' (value) {
      const ticketCoin = Number(value) || 0
      if (this.latestTicketCoin !== null && this.latestTicketCoin === ticketCoin) {
        this.latestTicketCoin = null
      }
      if (this.betDialog.show) {
        this.betDialog.ticketCoin = this.currentTicketCoin
      }
    }
  },
  created () {
    this.load().finally(() => {
      this.loadCelebrations()
    })
  },
  methods: {
    roundLabel,
    async load (preferredKey, options = {}) {
      const preserveScroll = !!options.preserveScroll
      const scrollY = preserveScroll ? window.pageYOffset : 0
      if (!preserveScroll) this.state = 'loading'
      try {
        const currentKey = preferredKey || this.activeKey
        const data = unwrapResponse(await this.getMatchesWithRetry())
        this.matches = Array.isArray(data) ? data : []
        if (this.celebrate.items.length) {
          this.celebrate.items = this.hydrateCelebrationItems(this.celebrate.items)
        }
        this.$emit('preload-flags', teamLogosFromMatches(this.matches, 1))
        this.activeKey = this.resolveGroupKey(currentKey) || this.pickInitialKey()
        this.state = this.matches.length ? 'success' : 'empty'
        this.scrollActiveIntoView()
        if (preserveScroll) this.restoreScroll(scrollY)
      } catch (error) {
        if (!preserveScroll) this.state = 'error'
      }
    },
    async getMatchesWithRetry () {
      const options = {
        skipToast: true,
        skipToastOnStatus0: true
      }
      try {
        return await getMatches(options)
      } catch (error) {
        const isNetworkError = error && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')
        const isStatus0 = error && error.request && error.request.status === 0
        if (!isNetworkError && !isStatus0) throw error
        await new Promise(resolve => window.setTimeout(resolve, 300))
        return getMatches(options)
      }
    },
    async loadCelebrations () {
      try {
        const data = unwrapResponse(await getCelebrations({
          skipToast: true,
          skipToastOnStatus0: true
        }))
        if (Array.isArray(data) && data.length) {
          this.celebrate.items = this.hydrateCelebrationItems(data)
          this.celebrate.show = true
          this.$emit('refresh-entry')
        }
      } catch (error) {}
    },
    hydrateCelebrationItems (items) {
      return items.map(item => {
        const match = this.findCelebrationMatch(item)
        if (!match) return item
        return Object.assign({}, match, item, {
          deadline_time: item.deadline_time || match.deadline_time
        })
      })
    },
    findCelebrationMatch (item) {
      if (!item) return null
      const keys = [
        item.match_key,
        item.match_id,
        item.schedule_id,
        item.id
      ].filter(value => value !== undefined && value !== null && value !== '')
      return this.matches.find(match => keys.some(key => {
        const text = String(key)
        return String(match.match_key) === text ||
          String(match.match_id) === text ||
          String(match.schedule_id) === text ||
          String(match.id) === text
      }))
    },
    pickInitialKey () {
      const todayKey = this.localDateKey(new Date())
      const todayMatch = this.matches.find(item => this.matchDateKey(item) === todayKey)
      if (todayMatch) return this.tabGroupKey(todayMatch)

      const now = Date.now() / 1000
      const upcomingMatch = this.matches.find(item => Number(item && item.deadline_time) >= now)
      return this.tabGroupKey(upcomingMatch || this.matches[0])
    },
    selectGroup (key) {
      this.activeKey = key
      this.scrollActiveIntoView()
    },
    scrollActiveIntoView () {
      this.$nextTick(() => {
        const tabs = this.$refs.tabs
        const refs = this.$refs[`tab-${this.activeKey}`]
        const target = Array.isArray(refs) ? refs[0] : refs
        if (!tabs || !target) return
        const left = target.offsetLeft - (tabs.clientWidth - target.clientWidth) / 2
        tabs.scrollTo({ left: Math.max(left, 0), behavior: 'smooth' })
      })
    },
    restoreScroll (scrollY) {
      this.$nextTick(() => {
        window.scrollTo(0, scrollY)
      })
    },
    async openBet ({ match, side }) {
      if (!isBettable(match) || this.balanceLoading) return
      this.balanceLoading = true
      Toast.loading({
        message: '加载中...',
        forbidClick: true,
        duration: 0
      })
      try {
        await waitForNativeTouchEnd()
        const balanceData = unwrapResponse(await getBalance())
        const coinBalance = Number(balanceData && balanceData.coin_balance) || 0
        Toast.clear()
        this.betDialog = {
          show: true,
          match,
          side,
          ticketCoin: this.currentTicketCoin,
          coinBalance
        }
      } catch (error) {
        Toast.clear()
        Toast(error.message || '余额加载失败，请稍后重试')
      } finally {
        this.balanceLoading = false
      }
    },
    async refreshBalance () {
      const balanceData = unwrapResponse(await getBalance())
      const coinBalance = Number(balanceData && balanceData.coin_balance) || 0
      this.betDialog = Object.assign({}, this.betDialog, { coinBalance })
      return coinBalance
    },
    requestRecharge () {
      this.$emit('recharge', async success => {
        if (!success) return
        try {
          await this.refreshBalance()
        } catch (error) {}
      })
    },
    selectedSide (match) {
      if (!this.betDialog.show || !this.betDialog.match) return 0
      return this.betDialog.match.match_key === match.match_key ? this.betDialog.side : 0
    },
    closeBet () {
      if (this.submitting) return
      this.betDialog.show = false
    },
    async confirmBet () {
      if (!this.betDialog.match || this.submitting) return
      const ticketCoin = this.currentTicketCoin
      if (this.betDialog.ticketCoin !== ticketCoin) {
        Toast('门票价格有变动，请确认后再参与')
        this.betDialog.ticketCoin = ticketCoin
        return
      }
      if ((this.betDialog.coinBalance || 0) < ticketCoin) {
        Toast('余额不足')
        this.requestRecharge()
        return
      }
      this.submitting = true
      try {
        await waitForNativeTouchEnd()
        const matchKey = this.betDialog.match.match_key
        const data = unwrapResponse(await bet({
          match_key: matchKey,
          chosen_side: this.betDialog.side,
          ticket_coin: ticketCoin,
          idempotency_key: createIdempotencyKey('bet')
        }))
        Toast('竞猜成功')
        this.latestTicketCoin = null
        this.betDialog.show = false
        this.$emit('bet-success', data)
        await this.load(matchKey, { preserveScroll: true })
      } catch (error) {
        if (Number(error.code) === 602) {
          Toast('余额不足')
          this.requestRecharge()
        } else if (Number(error.code) === 633) {
          Toast('本场已参与')
          const matchKey = this.betDialog.match.match_key
          this.betDialog.show = false
          await this.load(matchKey, { preserveScroll: true })
        } else if (Number(error.code) === 634) {
          Toast('门票价格有变动，请确认后再参与')
          const latestTicket = error.data && Number(error.data.ticket_coin)
          if (latestTicket) {
            this.latestTicketCoin = latestTicket
            this.betDialog.ticketCoin = latestTicket
          }
          this.$emit('refresh-entry')
        } else if (Number(error.code) === 631) {
          Toast('暂无法参与本次活动')
          this.$emit('blocked')
        } else {
          Toast(error.message || '网络异常，请稍后确认是否参与成功')
        }
      } finally {
        this.submitting = false
      }
    },
    async goLottery () {
      this.celebrate.show = false
      await waitForNativeTouchEnd()
      this.$emit('go-lottery')
    },
    tabDateLabel (match) {
      const seconds = Number(match && match.deadline_time)
      if (!seconds) return '--'
      const date = new Date(seconds * 1000)
      return `${date.getMonth() + 1}/${date.getDate()}`
    },
    localDateKey (date) {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    },
    matchDateKey (match) {
      const seconds = Number(match && match.deadline_time)
      if (!seconds) return ''
      return this.localDateKey(new Date(seconds * 1000))
    },
    tabStageLabel (match) {
      if (!match) return ''
      return roundStageName(match.round) || match.stage_name || match.round_name || roundLabel(match)
    },
    tabGroupKey (match) {
      if (!match) return ''
      return `${this.tabDateLabel(match)}|${this.tabStageLabel(match)}`
    },
    resolveGroupKey (key) {
      if (!key) return ''
      if (this.matchGroups.some(group => group.key === key)) return key
      const match = this.matches.find(item => item.match_key === key)
      return this.tabGroupKey(match)
    }
  }
}
</script>

<style scoped lang="scss">
.hall {
  padding: 12px 12px calc(136px + env(safe-area-inset-bottom, 0px));
}

.hall__tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.hall__tabs button {
  display: flex;
  flex: 0 0 82px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 82px;
  height: 64px;
  padding: 0;
  box-sizing: border-box;
  border: 0;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(180deg, rgba(255, 255, 255, .22), rgba(255, 255, 255, .08));
  font-family: "PingFang SC", sans-serif;
}

.hall__tab-date {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.hall__tab-stage {
  margin-top: 3px;
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;
}

.hall__tabs button.active {
  color: #fff;
  border: 2px solid #ffe669;
  background: linear-gradient(180deg, rgba(33, 112, 55, .82), rgba(22, 86, 39, .78));
}

.hall__cards {
  display: grid;
  gap: 14px;
}

.hall__loading,
.hall__empty,
.hall__error {
  padding: 42px 0;
  color: rgba(255, 255, 255, .72);
  text-align: center;
  font-size: 14px;
}

.hall__error button {
  margin-left: 10px;
  border: 0;
  color: #ffdf59;
  background: transparent;
  font-weight: 800;
}

</style>
